import { apiClient } from '@/api/client';
import type { ApiResponse } from '@/api/types';

export interface MrpRun {
  id: string;
  runDate: string;
  planningHorizonDays: number;
  status: 'DRAFT' | 'APPROVED' | 'CLOSED' | 'COMPLETED' | string;
  warehouseId?: string | null;
  warehouseName?: string | null;
  runBy?: string | null;
  runByName?: string | null;
  createdAt?: string;
  runNumber?: string;
  note?: string;
  itemsCount?: number;
  shortagesCount?: number;
  suggestionsCount?: number;
  shortageDelta?: number | null;
  startDate?: string;
  endDate?: string;
}

export interface MrpRequirementRow {
  itemId: string;
  itemName: string;
  itemCode?: string;
  itemType?: string;
  leadTimeDays?: number;
  hasBom?: boolean;
  purchaseRate?: number;
  grossRequirement: number;
  stockOnHand: number;
  stockOnOrder: number;
  safetyStock: number;
  netRequirement: number;
  plannedOrderQty: number;
  orderByDate: string | null;
  requiredDate: string | null;
  needsOrder: boolean;
}

export interface MrpPlannedOrder {
  id: string;
  mrp_run_id: string;
  warehouse_id?: string | null;
  warehouseId?: string | null;
  warehouseName?: string | null;
  item_id: string;
  item_name: string;
  itemCode?: string;
  supplier_id: string | null;
  supplierName?: string | null;
  planned_qty: number;
  unit?: string;
  planned_date: string | null;
  requiredDate?: string | null;
  purchaseRate?: number;
  leadTimeDays?: number;
  priority?: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW';
  status: 'PLANNED' | 'PENDING' | 'APPROVED' | 'CONVERTED' | 'REJECTED';
  converted_po_id: string | null;
  created_at?: string;
}

export interface MrpProductionSuggestion {
  id: string;
  mrp_run_id?: string;
  warehouse_id?: string | null;
  warehouseId?: string | null;
  warehouseName?: string | null;
  item_id?: string;
  item_name: string;
  itemCode: string;
  bomCode: string;
  bomId?: string;
  qty: number;
  unit: string;
  startDate: string;
  dueDate: string;
  estCost: number;
  leadTimeDays: number;
  priority: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW';
  status: 'PLANNED' | 'PENDING' | 'APPROVED' | 'CONVERTED' | 'REJECTED';
}

export interface MrpActionMessage {
  id: string;
  mrp_run_id: string;
  message_type: 'ORDER_NOW' | 'RESCHEDULE' | 'CANCEL' | 'EXPEDITE';
  item_id: string;
  item_name: string;
  message: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  is_resolved: boolean;
  resolved_at: string | null;
  created_at: string;
}

// Material position of one open production order, as found by an MRP run
export interface MrpProductionOrderShortage {
  id: string;
  productionOrderId: string;
  poNumber: string;
  orderStatus: string;
  productName: string | null;
  orderQty: number;
  plannedStartDate: string | null;
  itemId: string;
  itemName: string;
  itemCode: string | null;
  unit: string;
  requiredQty: number;
  issuedQty: number;
  outstandingQty: number;
  coveredQty: number;
  shortageQty: number;
  stockOnHand: number;
  stockOnOrder: number;
  orderByDate: string | null;
  leadTimeDays: number | null;
  atRisk: boolean;
}

export interface MrpDashboardData {
  latestRunId: string | null;
  lastRunDate?: string | null;
  // Documents created or changed after the latest run
  changesSinceRun?: { salesOrders: number; purchaseOrders: number; goodsReceipts: number; productionOrders: number; total: number } | null;
  cards: { itemsCovered: number; atRisk: number; late: number; actionNeeded: number };
  atRiskItems: Array<{ itemId: string; itemName: string; net: number; orderByDate: string | null; riskLevel: 'AT_RISK' | 'LATE' }>;
  recentRuns: MrpRun[];
}

export interface RunMrpWarehouseSummary {
  mrpRunId: string;
  warehouseId: string | null;
  warehouseName: string;
  requirementCount: number;
  purchaseSuggestionCount: number;
  productionSuggestionCount: number;
}

export interface RunMrpResult {
  /** Run with the most suggestions — the one to open after an all-warehouse run */
  mrpRunId: string;
  /** One entry per warehouse planned (all active warehouses when none is selected) */
  runs?: RunMrpWarehouseSummary[];
  requirements: unknown[];
  plannedOrders: unknown[];
  productionSuggestions?: unknown[];
  actionMessages: unknown[];
  autoCreated?: {
    purchaseOrders: Array<{ poId: string; poNumber: string }>;
    productionOrders: string[];
    skipped: string[];
  };
}

const BASE = '/api/v1/manufacturing/mrp';

export const mrpService = {
  run: async (payload: {
    planningHorizonDays?: number;
    fromDate?: string;
    toDate?: string;
    warehouseId?: string;
    demandOptions?: {
      openSalesOrders?: boolean;
      productionOrders?: boolean;
      safetyStock?: boolean;
      reorderPoint?: boolean;
    };
    runOptions?: {
      autoCreateDraftPO?: boolean;
      autoCreateDraftProdOrder?: boolean;
      considerScheduledReceipts?: boolean;
    };
    salesOrderIds?: string[];
  }): Promise<RunMrpResult> => {
    const { data } = await apiClient.post<ApiResponse<RunMrpResult>>(`${BASE}/run`, payload);
    return data.data;
  },

  getRuns: async (params?: { warehouseId?: string }): Promise<MrpRun[]> => {
    const { data } = await apiClient.get<ApiResponse<MrpRun[]>>(`${BASE}/runs`, { params });
    return data.data;
  },

  getRunById: async (id: string, warehouseId?: string) => {
    const { data } = await apiClient.get<ApiResponse<{ run: MrpRun; requirements: unknown[]; plannedOrders: MrpPlannedOrder[]; actionMessages: MrpActionMessage[] }>>(`${BASE}/runs/${id}`, { params: { warehouseId } });
    return data.data;
  },

  getRequirements: async (runId: string, warehouseId?: string): Promise<MrpRequirementRow[]> => {
    const { data } = await apiClient.get<ApiResponse<MrpRequirementRow[]>>(`${BASE}/requirements`, { params: { runId, warehouseId } });
    return data.data;
  },

  getPlannedOrders: async (params: { runId?: string; status?: string; warehouseId?: string } = {}): Promise<MrpPlannedOrder[]> => {
    const { data } = await apiClient.get<ApiResponse<MrpPlannedOrder[]>>(`${BASE}/planned-orders`, { params });
    return data.data;
  },

  approvePlannedOrder: async (id: string): Promise<MrpPlannedOrder> => {
    const { data } = await apiClient.post<ApiResponse<MrpPlannedOrder>>(`${BASE}/planned-orders/${id}/approve`, {});
    return data.data;
  },

  rejectPlannedOrder: async (id: string): Promise<MrpPlannedOrder> => {
    const { data } = await apiClient.post<ApiResponse<MrpPlannedOrder>>(`${BASE}/planned-orders/${id}/reject`, {});
    return data.data;
  },

  batchApprovePlannedOrders: async (ids: string[]): Promise<MrpPlannedOrder[]> => {
    const { data } = await apiClient.post<ApiResponse<MrpPlannedOrder[]>>(`${BASE}/planned-orders/batch-approve`, { ids });
    return data.data;
  },

  batchRejectPlannedOrders: async (ids: string[]): Promise<MrpPlannedOrder[]> => {
    const { data } = await apiClient.post<ApiResponse<MrpPlannedOrder[]>>(`${BASE}/planned-orders/batch-reject`, { ids });
    return data.data;
  },

  updatePlannedOrder: async (id: string, payload: { planned_qty?: number; supplier_id?: string | null }): Promise<MrpPlannedOrder> => {
    const { data } = await apiClient.patch<ApiResponse<MrpPlannedOrder>>(`${BASE}/planned-orders/${id}`, payload);
    return data.data;
  },

  getProductionSuggestions: async (params: { runId?: string; warehouseId?: string } = {}): Promise<MrpProductionSuggestion[]> => {
    const { data } = await apiClient.get<ApiResponse<MrpProductionSuggestion[]>>(`${BASE}/production-suggestions`, { params });
    return data.data;
  },

  approveProductionSuggestion: async (id: string): Promise<any> => {
    const { data } = await apiClient.post<ApiResponse<any>>(`${BASE}/production-suggestions/${id}/approve`, {});
    return data.data;
  },

  rejectProductionSuggestion: async (id: string): Promise<any> => {
    const { data } = await apiClient.post<ApiResponse<any>>(`${BASE}/production-suggestions/${id}/reject`, {});
    return data.data;
  },

  convertProductionSuggestions: async (payload: { suggestionIds?: string[]; suggestions?: any[]; warehouseId?: string }): Promise<any[]> => {
    const { data } = await apiClient.post<ApiResponse<any[]>>(`${BASE}/production-suggestions/convert`, payload);
    return data.data;
  },

  convertPlannedOrders: async (plannedOrderIds: string[], warehouseId?: string): Promise<Array<{ poId: string; poNumber: string; supplierId: string; itemCount: number }>> => {
    const { data } = await apiClient.post<ApiResponse<Array<{ poId: string; poNumber: string; supplierId: string; itemCount: number }>>>(`${BASE}/planned-orders/convert`, { plannedOrderIds, warehouseId });
    return data.data;
  },

  getActionMessages: async (params: { runId?: string; resolved?: boolean; warehouseId?: string } = {}): Promise<MrpActionMessage[]> => {
    const { data } = await apiClient.get<ApiResponse<MrpActionMessage[]>>(`${BASE}/action-messages`, { params });
    return data.data;
  },

  resolveActionMessage: async (id: string): Promise<MrpActionMessage> => {
    const { data } = await apiClient.put<ApiResponse<MrpActionMessage>>(`${BASE}/action-messages/${id}/resolve`, {});
    return data.data;
  },

  /** BOM Explosion → production suggestions for selected in-house (semi-finished) items */
  createProductionSuggestionsFromBOM: async (payload: {
    warehouseId?: string;
    sourceProductionOrderId?: string;
    items: Array<{ itemId: string; itemName: string; shortQty: number; requiredQty: number }>;
  }): Promise<{ mrpRunId: string; created: Array<{ id: string; itemName: string; qty: number }>; skipped: string[] }> => {
    const { data } = await apiClient.post<ApiResponse<{ mrpRunId: string; created: Array<{ id: string; itemName: string; qty: number }>; skipped: string[] }>>(
      `${BASE}/production-suggestions/create-from-bom`,
      payload,
    );
    return data.data;
  },
  createFromBOM: async (payload: {
    bomId: string;
    bomCode?: string;
    warehouseId?: string;
    productName?: string;
    productionQty?: number;
    items: Array<{
      itemId: string;
      itemName: string;
      shortQty: number;
      requiredQty: number;
      unitPrice?: number;
      unit?: string;
    }>;
  }): Promise<{ mrpRunId: string; plannedOrdersCount: number }> => {
    const { data } = await apiClient.post<ApiResponse<{ mrpRunId: string; plannedOrdersCount: number }>>(
      `${BASE}/create-from-bom`,
      payload
    );
    return data.data;
  },

  getProductionOrderShortages: async (runId: string): Promise<MrpProductionOrderShortage[]> => {
    const { data } = await apiClient.get<ApiResponse<MrpProductionOrderShortage[]>>(`${BASE}/production-order-shortages`, { params: { runId } });
    return (data.data || []).map((r) => ({
      ...r,
      orderQty: Number(r.orderQty) || 0,
      requiredQty: Number(r.requiredQty) || 0,
      issuedQty: Number(r.issuedQty) || 0,
      outstandingQty: Number(r.outstandingQty) || 0,
      coveredQty: Number(r.coveredQty) || 0,
      shortageQty: Number(r.shortageQty) || 0,
      stockOnHand: Number(r.stockOnHand) || 0,
      stockOnOrder: Number(r.stockOnOrder) || 0,
    }));
  },

  getDashboard: async (params?: { warehouseId?: string }): Promise<MrpDashboardData> => {
    const { data } = await apiClient.get<ApiResponse<MrpDashboardData>>(`${BASE}/dashboard`, { params });
    return data.data;
  },
};
